import { Suspense, lazy } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { ErrorBoundary } from "../components/error/ErrorBoundary";
import { LoadingSpinner } from "../components/ui/LoadingSpinner";
import { Button } from "../components/ui";
import { Icons } from "../icons";
import { ProtectedRoute } from "../features/auth/components/ProtectedRoute";

// Lazy load pages for code splitting
const AppLayout = lazy(() => import("../components/layout/AppLayout"));

// Auth pages
const LoginPage = lazy(() => import("../features/auth/components/LoginPage"));
const ResetPasswordPage = lazy(
  () => import("../features/auth/components/ResetPasswordPage"),
);

// Feature pages
const Dashboard = lazy(() => import("../features/dashboard/Dashboard"));
const BorefieldList = lazy(
  () => import("../features/borefields/BorefieldList"),
);
const BorefieldDetail = lazy(
  () => import("../features/borefields/BorefieldDetail"),
);
const ReportsView = lazy(() => import("../features/reports/ReportsView"));
const RigsView = lazy(() => import("../features/rigs/RigsView"));
const SettingsView = lazy(() => import("../features/settings/SettingsView"));
const FormationEditorScreen = lazy(
  () => import("../features/formations/screens/FormationEditorScreen"),
);
const TrtV2View = lazy(() => import("../features/trt/components/TrtView"));

// Time Reports — 3 URL patterns (PR2-B.4)
// Pattern A: /borefields/:borefieldId/jobs/:jobId/time-reports
// Pattern B: /jobs/:jobId/time-reports
// Pattern C: /time-reports?jobId=<n>  (global filter view)
const TimeReportsView = lazy(() =>
  import('../features/time-reports/components/TimeReportsView').then((m) => ({
    default: m.TimeReportsView,
  })),
);

// Unauthorized page component
const UnauthorizedPage = () => {
  const navigate = useNavigate();
  return (
    <div className="unauthorized-page">
      <div className="unauthorized-content">
        <Icons.Shield />
        <h1>Access Denied</h1>
        <p>You don&apos;t have permission to access this page.</p>
        <Button onClick={() => navigate(-1)}>Go Back</Button>
      </div>
    </div>
  );
};

// Loading fallback component
const PageLoader = () => (
  <div className="page-loader">
    <LoadingSpinner size="large" />
    <span>Loading...</span>
  </div>
);

// Root layout with suspense (unprotected, for use in protected wrapper)
const RootLayout = () => (
  <ErrorBoundary>
    <Suspense fallback={<PageLoader />}>
      <AppLayout>
        <Suspense fallback={<PageLoader />}>
          <Outlet />
        </Suspense>
      </AppLayout>
    </Suspense>
  </ErrorBoundary>
);

// Protected root layout - requires authentication
const ProtectedRootLayout = () => (
  <ProtectedRoute>
    <RootLayout />
  </ProtectedRoute>
);

// Route configuration
// eslint-disable-next-line react-refresh/only-export-components
export const router = createBrowserRouter([
  // Auth routes (outside main layout)
  {
    path: "/login",
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <LoginPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    // Password-reset email-link landing. Public (no auth required).
    // The email contains /reset-password?token=...; this page reads the
    // token from the query string and POSTs it to
    // ${VITE_AUTH_URL}/v1/auth/password-reset/confirm together with the
    // new password the user typed.
    path: "/reset-password",
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <ResetPasswordPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: "/unauthorized",
    element: (
      <ErrorBoundary>
        <Suspense fallback={<PageLoader />}>
          <UnauthorizedPage />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  // Main app routes (with layout, protected)
  {
    path: "/",
    element: <ProtectedRootLayout />,
    children: [
      {
        index: true,
        element: <Navigate to="/borefields" replace />,
      },
      {
        path: "dashboard",
        element: <Dashboard />,
      },
      {
        path: "borefields",
        children: [
          {
            index: true,
            element: <BorefieldList />,
          },
          {
            path: ":borefieldId",
            element: <BorefieldDetail />,
          },
          {
            path: ":borefieldId/bore/:boreId",
            element: <BorefieldDetail />,
          },
          {
            path: ":borefieldId/bore/:boreId/job/:jobId",
            element: <BorefieldDetail />,
          },
          // Time Reports — Pattern A: /borefields/:borefieldId/jobs/:jobId/time-reports
          {
            path: ':borefieldId/jobs/:jobId/time-reports',
            element: <TimeReportsView />,
          },
        ],
      },
      {
        path: "reports",
        element: <ReportsView />,
      },
      // Time Reports — Pattern C: /time-reports?jobId=<n>
      {
        path: 'time-reports',
        element: <TimeReportsView />,
      },
      // Time Reports — Pattern B: /jobs/:jobId/time-reports
      {
        path: 'jobs/:jobId/time-reports',
        element: <TimeReportsView />,
      },
      {
        path: "rigs",
        element: <RigsView />,
      },
      {
        // Mud-log report editor. Page-based (not modal) per PO directive
        // 2026-06-01. ReportEditModal redirects here for mud-log report
        // type; route stays even though the sidebar test shortcut is gone.
        path: "formation-editor",
        element: <FormationEditorScreen />,
      },
      {
        // TRT v2 (Beta) — R_o-k Bourdet TRT, ported 1:1 (UX, units, math).
        // Per PO 2026-06-02 "test, then replace" — this is the test
        // surface; the existing BourdetTRTModal in components/Modals/
        // Thermal/Bourdet/ is the replacement target once PO signs off.
        // Backend API reconciliation (R_o-k /api/trt/* vs BIOT-k
        // /api/trt/bourdet/*) is the next commit — Steps 1-2 (bore
        // parameters, formation seeding) render without backend calls
        // and are exerciseable for layout/units/typography validation
        // right now.
        path: "trt-v2",
        element: <TrtV2View />,
      },
      {
        path: "settings",
        element: (
          <ProtectedRoute anyPermission={["admin:settings", "admin:users"]}>
            <SettingsView />
          </ProtectedRoute>
        ),
      },
    ],
  },
]);

// Router component for use in providers
export const AppRouter = () => <RouterProvider router={router} />;
